// src/pages/RegisterPage.js

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Logo from '../components/Logo';
import LanguageSwitcher from '../components/LanguageSwitcher';

function RegisterPage({ csrfToken }) {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [token, setToken] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Helper: lang aus hash-URL lesen (HashRouter-kompatibel)
  const getLangFromHash = () => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash) return null;
    const qs = hash.split('?')[1] || '';
    const p = new URLSearchParams(qs);
    return p.get('lang');
  };

  // ✅ NEU: generischer Helper für HashRouter-Params (token/role/data etc.)
  const getParamFromHash = (key) => {
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    if (!hash) return null;
    const qs = hash.split('?')[1] || '';
    const p = new URLSearchParams(qs);
    return p.get(key);
  };

  // 1) Sprache aus URL (?lang=de|en) priorisieren, sonst aus localStorage.
  useEffect(() => {
    let urlLang = searchParams.get('lang');
    if (!urlLang) urlLang = getLangFromHash();

    const savedLang =
      localStorage.getItem('language') || localStorage.getItem('preferred_lang');

    const norm = (v) =>
      v && typeof v === 'string' ? v.toLowerCase().slice(0, 2) : null;

    const desired = norm(urlLang) || norm(savedLang);

    if (desired === 'de' || desired === 'en') {
      if (i18n.language !== desired) {
        i18n.changeLanguage(desired);
      }
      try {
        localStorage.setItem('language', desired);
        localStorage.setItem('preferred_lang', desired);
      } catch {}
      axios.defaults.headers.common['Accept-Language'] = desired;
    } else {
      // Fallback: aktuellen i18n-Stand als Header setzen
      axios.defaults.headers.common['Accept-Language'] = i18n.language;
    }
  }, [searchParams, i18n]);

  // 2) Wenn sich i18n-Sprache später ändert (z. B. via Switcher), Header synchron halten.
  useEffect(() => {
    axios.defaults.headers.common['Accept-Language'] = i18n.language;
    try {
      localStorage.setItem('language', i18n.language);
      localStorage.setItem('preferred_lang', i18n.language);
    } catch {}
  }, [i18n.language]);

  // 2b) Falls ein Invite-Token in der URL steckt, aber auf dem Gerät
  // noch eine Session aktiv ist, einmal sauber abmelden und danach
  // automatisch auf diese Seite zurückkehren.
  useEffect(() => {
    const tokenInUrl = searchParams.get('token') || getParamFromHash('token');

    if (!tokenInUrl) return;

    (async () => {
      try {
        const r = await fetch('/auth/whoami', { credentials: 'include' });
        const who = r.ok ? await r.json() : null;
        if (who?.user) {
          // serverseitig Session killen und exakt auf dieselbe URL zurück
          const ret = encodeURIComponent(window.location.href);
          window.location.href = `/auth/logout?return=${ret}`;
        }
      } catch {
        /* ignore */
      }
    })();
  }, [searchParams]);

  // 3) Token & Rolle aus URL extrahieren (unterstützt "data"-Payload oder token/role einzeln)
  //    +++ NEU: Paket aus URL/JSON lesen und für Onboarding vormerken + Token sicherheitshalber hinterlegen +++
  useEffect(() => {
    setErrorMessage('');

    // Hilfsfunktion: Paket aus URL extrahieren (verschiedene Synonyme unterstützen)
    const readPackageFromUrl = () => {
      const p =
        searchParams.get('package') ||
        searchParams.get('pkg') ||
        searchParams.get('plan');
      return p && typeof p === 'string' ? p : null;
    };

    const dataParam = searchParams.get('data') || getParamFromHash('data');
    if (dataParam) {
      try {
        const decoded = atob(decodeURIComponent(dataParam));
        const parsed = JSON.parse(decoded);

        if (parsed.token && parsed.role) {
          setToken(parsed.token);
          setRole(parsed.role);

          // Paket auch aus JSON akzeptieren, falls vorhanden
          const pkgFromJson =
            parsed.package || parsed.pkg || parsed.plan || readPackageFromUrl();
          if (pkgFromJson) {
            try {
              localStorage.setItem('hr_invite_package', String(pkgFromJson));
            } catch {}
          }

          // Token zusätzlich vormerken (optional nützlich für Onboarding)
          try {
            localStorage.setItem('hr_invite_token', String(parsed.token));
          } catch {}
        } else {
          setErrorMessage(t('register.incompleteLink'));
        }
      } catch (e) {
        console.error('Ungültige Registrierungsdaten in URL:', e);
        setErrorMessage(t('register.invalidLink'));
      }
    } else {
      // Fallback für alte Links
      const tokenFallback = searchParams.get('token') || getParamFromHash('token');
      const roleFallback = searchParams.get('role') || getParamFromHash('role');

      if (tokenFallback && roleFallback) {
        setToken(tokenFallback);
        setRole(roleFallback);

        // +++ NEU: Paket aus URL-Parametern abgreifen und in LocalStorage vormerken +++
        const pkgFromUrl = readPackageFromUrl();
        if (pkgFromUrl) {
          try {
            localStorage.setItem('hr_invite_package', String(pkgFromUrl));
          } catch {}
        }

        // Token zusätzlich vormerken (optional)
        try {
          localStorage.setItem('hr_invite_token', String(tokenFallback));
        } catch {}
      } else {
        setErrorMessage(t('register.incompleteLink'));
      }
    }
  }, [searchParams, t]);

  async function handleRegister(e) {
    e.preventDefault();
    setErrorMessage('');

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/mentoring/register`,
        {
          token,
          // Historisches Verhalten beibehalten:
          // fullName wird aktuell mit der E-Mail gefüllt. (Backend nutzt primär den Token.)
          fullName: email
        },
        {
          withCredentials: true,
          headers: {
            'X-CSRF-Token': csrfToken,
            'Accept-Language': i18n.language
          }
        }
      );

      // Sprache in die Ziel-Route mitnehmen
      const params = new URLSearchParams({ lang: i18n.language });

      // Rolle robust vom Server holen (Session nach Registration)
      let effRole = role;
      try {
        const who = await axios.get('/auth/whoami', {
          withCredentials: true,
          headers: { 'Accept-Language': i18n.language }
        });
        effRole = who?.data?.role || who?.data?.user?.role || effRole;
      } catch {
        /* ignore – Fallback auf lokale role */
      }

      // Falls Paket im LocalStorage liegt, für HR-Onboarding mitgeben
      const storedPkg = (() => {
        try {
          return localStorage.getItem('hr_invite_package');
        } catch {
          return null;
        }
      })();

      // Navigations-Entscheidung anhand der effektiven Rolle
      if (['hr', 'hr_admin', 'hr_user'].includes(effRole)) {
        if (storedPkg) {
          params.set('package', storedPkg);
        }
        navigate('/onboarding-hr?' + params.toString());
      } else if (effRole === 'mentee') {
        navigate('/onboarding-mentee?' + params.toString());
      } else if (effRole === 'mentor') {
        navigate('/onboarding-mentor?' + params.toString());
      } else {
        navigate('/dashboard?' + params.toString());
      }
    } catch (err) {
      console.error('Fehler bei Registrierung:', err);
      const error =
        err.response?.data?.error ||
        (typeof err.response?.data === 'string' ? err.response.data : null) ||
        t('register.registrationFailed');
      setErrorMessage(error);
    }
  }

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6 text-white relative">
      {/* Language Switcher oben rechts */}
      <LanguageSwitcher />

      <div className="mb-10">
        <Logo variant="wordmark" size="xxxl" />
      </div>

      <h2 className="text-3xl md:text-4xl font-bold mb-8">
        {t('register.heading')}
      </h2>

      {errorMessage && (
        <div className="bg-red-600 text-white p-4 rounded-2xl mb-6 w-full max-w-md text-center">
          {errorMessage}
        </div>
      )}

      <form
        onSubmit={handleRegister}
        className="bg-gray-800 p-8 rounded-2xl shadow-md w-full max-w-md"
      >
        <div className="mb-6">
          <label className="block mb-2 font-semibold">
            {t('register.email')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="w-full p-2 bg-gray-700 rounded text-white"
            autoComplete="email"
          />
        </div>

        <div className="mb-6">
          <label className="block mb-2 font-semibold">
            {t('register.role')}
          </label>
          <input
            type="text"
            value={role}
            readOnly
            className="w-full p-2 bg-gray-700 rounded text-white"
          />
        </div>

        <button
          type="submit"
          className="w-full bg-mentixxgreen hover:bg-green-400 text-black font-bold py-2 rounded-2xl transition"
        >
          {t('register.submit')}
        </button>

        <p className="text-sm text-gray-400 mt-6 text-center">
          {t('register.info')}
        </p>
      </form>
    </div>
  );
}

export default RegisterPage;
